import React, { useCallback, useEffect, Suspense, useMemo } from 'react';
import {
  PlaidEmbeddedLink,
  PlaidLinkOnSuccess,
  PlaidLinkOnEvent,
  PlaidLinkOnExit,
  PlaidLinkStableEvent,
  PlaidLinkOnEventMetadata,
  PlaidLinkOnLoad,
} from 'react-plaid-link';
import { useObserver } from 'mobx-react-lite';
import { useQuery } from '@apollo/client';

import { useStores } from 'contexts/stores';
import { StartAggregatorLinkV2Document } from 'src/gql/graphql';
import {
  Aggregator,
  DUTCHIE_PAY_QUERY_PARMAS_KEY,
  EnrollmentSteps,
  IS_E2E,
  PLAID_STORAGE_KEY,
  PlaidLinkCustomizationNames,
} from 'src/payments/constants';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { usePlaidLinkUtils } from 'src/payments/hooks/use-plaid-link-utils/use-plaid-link-utils';
import {
  PLAID_LINK_TO_BANK_CONNECTION_CONTEXT,
  useDutchiePayAnalytics,
} from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import { useTrackEnrollmentStepViewed } from '../hooks/use-dutchie-pay-analytics/use-track-enrollment-step-viewed';

const MockAggregatorWidget = React.lazy(() => import('./mock-aggregator-widget'));

type PlaidEmbeddedSearchProps = {
  handleEmbeddedDutchiePayLink?: () => void;
  handleOnError?: () => void;
  handleOnLinkExit?: () => void;
  linkCustomizationName?: PlaidLinkCustomizationNames;
  height?: number | string;
  width?: number | string;
  onLoadingStateChange?: (state: { isLoading: boolean; hasToken: boolean }) => void;
  handleOnPlaidConnectBank?: () => void;
};

export const PlaidEmbeddedSearch = ({
  handleEmbeddedDutchiePayLink,
  handleOnError,
  handleOnLinkExit,
  linkCustomizationName = PlaidLinkCustomizationNames.DEFAULT,
  width,
  height = 350,
  onLoadingStateChange,
  handleOnPlaidConnectBank,
}: PlaidEmbeddedSearchProps): JSX.Element => {
  const { User } = useStores();
  const userFirstName = useObserver(() => User?.profile?.firstName);
  const userLastName = useObserver(() => User?.profile?.lastName);

  const { trackPlaidOnEvent, trackPlaidOnLoad, trackPlaidOnSuccess, trackPlaidOnExit } = useDutchiePayAnalytics();

  const connectionMethod = PLAID_LINK_TO_BANK_CONNECTION_CONTEXT[linkCustomizationName];
  const plaidEmbeddedSearchRef = useTrackEnrollmentStepViewed({
    step: EnrollmentSteps.connectBank,
    context: connectionMethod,
  });

  const { aggregatorLinkExited } = useDutchiePayEnrollment();

  const userEmail = useObserver(() => User?.email);
  const userPhone = useObserver(() => User?.profile?.phone);
  const missingRequiredUserInfo = !userFirstName || !userLastName || !userEmail || !userPhone;

  const {
    data: startAggregatorLinkData,
    loading: startAggregatorLinkLoading,
    refetch: refetchStartAggregatorLink,
  } = useQuery(StartAggregatorLinkV2Document, {
    variables: {
      aggregator: Aggregator.PLAID,
      firstName: userFirstName,
      lastName: userLastName,
      email: userEmail,
      phone: userPhone,
      paymentMethodId: '',
      linkCustomizationName,
    },
    skip: missingRequiredUserInfo,
    fetchPolicy: 'network-only',
  });

  const plaidLinkToken = startAggregatorLinkData?.startAggregatorLinkV2.plaid?.linkToken ?? '';

  const { handleSuccess } = usePlaidLinkUtils({
    handleEmbeddedDutchiePayLink,
    handleOnError,
    linkCustomizationName,
    handleOnPlaidConnectBank,
    refetchStartAggregatorLink,
  });

  /*
   * TODO:
   * Just rely on passing in whatever is passed for handleOnLinkExit
   * and remove the call to useDutchiePayEnrollment and aggregatorLinkExited
   */
  const onLinkExit = handleOnLinkExit ?? aggregatorLinkExited;

  const clearPlaidData = (): void => {
    localStorage.removeItem(DUTCHIE_PAY_QUERY_PARMAS_KEY);
    localStorage.removeItem(PLAID_STORAGE_KEY);
  };

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    (publicToken, metadata) => {
      trackPlaidOnSuccess({ eventMetadata: metadata, connectionMethod });
      void handleSuccess({ publicToken, metadata });
    },
    [handleSuccess, trackPlaidOnSuccess, connectionMethod]
  );

  const onEvent = useCallback<PlaidLinkOnEvent>(
    (eventName: PlaidLinkStableEvent, eventMetadata: PlaidLinkOnEventMetadata) => {
      trackPlaidOnEvent({ eventName, eventMetadata, connectionMethod });
    },
    [trackPlaidOnEvent, connectionMethod]
  );

  const onExit = useCallback<PlaidLinkOnExit>(
    (plaidError, eventMetadata) => {
      trackPlaidOnExit({ plaidError, eventMetadata, connectionMethod });
      clearPlaidData();
      onLinkExit();
    },
    [onLinkExit, trackPlaidOnExit, connectionMethod]
  );

  const onLoad = useCallback<PlaidLinkOnLoad>(() => {
    trackPlaidOnLoad({ connectionMethod });
  }, [trackPlaidOnLoad, connectionMethod]);

  const config = useMemo(
    () => ({
      token: plaidLinkToken,
      onSuccess,
      onEvent,
      onExit,
      onLoad,
    }),
    [plaidLinkToken, onSuccess, onEvent, onExit, onLoad]
  );

  useEffect(() => {
    onLoadingStateChange?.({
      isLoading: startAggregatorLinkLoading,
      hasToken: Boolean(plaidLinkToken),
    });
  }, [startAggregatorLinkLoading, plaidLinkToken, onLoadingStateChange]);

  if (IS_E2E) {
    return (
      <Suspense fallback={null}>
        <MockAggregatorWidget aggregator={Aggregator.PLAID} onSuccess={onSuccess} onExit={onExit} />
      </Suspense>
    );
  }

  return (
    <div
      ref={plaidEmbeddedSearchRef}
      style={{
        ...(width && { width }),
        height,
      }}
    >
      <PlaidEmbeddedLink
        {...config}
        style={{
          width: '100%',
          height: '100%',
        }}
      />
    </div>
  );
};

export const MemoizedPlaidEmbeddedSearch = React.memo(PlaidEmbeddedSearch);
